import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import { BackButtonPage } from "components/__new/common/BackButton";
import TabSettings from "components/__new/common/TabSettings";
import { ButtonLoader, Checkbox } from "components/__new/common/form";
import CurrencyFormat from "utils/currencyFormat";
import numberOrdinalSuffix from "utils/numberOrdinalSuffix";
import ModalAlert from "components/__new/common/ModalAlert";
import useSubAccounts from "hooks/useSubAccounts";
import ModalSwitchingAccount from "components/__new/subAccounts/ModalSwitchingAccount";
import useLoginInfo from "hooks/useLoginInfo";
import * as auth from "store/auth/user/action";


const Preview = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const { isOpenModalSwitchAccount, setIsOpenModalSwitchAccount, switchToSubAccount, subAccountSwitchAccount } = useSubAccounts();
    const { subAccountForm, addSubAccountLoading, subAccount } = useSelector((s) => s.auth.user);

    const [isOpenModalAlert, setIsOpenModalAlert] = useState(false);
    const companySizeArray = {
        "1": "Less than 50",
        "2": "50 - 100",
        "3": "100 - 200",
        "4": "200 and above",
    }
    const paymentFeeByArray = {
        "employee": "By Employee",
        "employer": "By Business",
        "shared": "Shared",
    }


    const submitForm = async e => {
        e.preventDefault();
        dispatch(auth.addSubAccount(subAccountForm));
    }


    const switchToSubAccountParams = (data) => {
        if (data){
            return {
                ...data,
                isEmailVerified: true,      // needs to be returned from the API
                isCompanyInfo: true,        // needs to be returned from the API
                isActive: true,             // needs to be returned from the API
            }
        }
        return data;
    }


    useEffect(() => {
        if (userInfo?.employer?.accountType === "sub" && Object.keys(userInfo?.mainAccount).length === 0){
            history.replace("/401");
        }

        if (!("sameCompany" in subAccountForm)){
            history.push("/account/add");
        }
        // eslint-disable-next-line
    }, [])


    useEffect(() => {
        if (addSubAccountLoading === false && subAccount?.status === "success"){
            setIsOpenModalAlert(true);
            const params = {
                page: 1,
                limit: 50,
                download: false
            };
            dispatch(auth.getSubAccounts(params));
        }
        // eslint-disable-next-line
    }, [subAccount])


    return (
        <div>


            <DynamicHeadTag
                headerText="Preview Details | Sub Accounts"
                headerDescription={null}
            />


            <ModalAlert
                successText="Business added successfully"
                subText={subAccountForm?.newUser ? "Please accept the email invite sent to " + subAccountForm.email + ", so as to have access to the newly added business" : ""}
                isOpenModalAlert={isOpenModalAlert}
                setIsOpenModalAlert={setIsOpenModalAlert}
                button1={{
                    "text": "Switch to the new business",
                    "class": "btn btn-lg btn-ep-primary btn-block",
                    "click": () => switchToSubAccount(switchToSubAccountParams(subAccount?.subAccount)),
                }}
                button2={{
                    "text": "Add Another Business",
                    "class": "btn btn-lg btn-block text-ep-primary hover:bg-gray-100 font-medium",
                    "click": () => {setIsOpenModalAlert(false); history.push("/account/add")},
                }}
                dismissText={null}
                closeDialog={() => {}}
            />


            <ModalSwitchingAccount
                subAccount={subAccountSwitchAccount}
                isOpenModalSwitchAccount={isOpenModalSwitchAccount}
                setIsOpenModalSwitchAccount={setIsOpenModalSwitchAccount}
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title flex">
                        <BackButtonPage />
                        Add Business
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>


            <div className="mt-6 box">
                <div className="border-b">
                    <TabSettings options={[
                        {
                            title: <span>[1] Business Details</span>,
                            type: "link",
                            url: "/account/add/company",
                        },
                        {
                            title: <span>[2] Admin Details</span>,
                            type: "link",
                            url: "/account/add/user",
                        },
                        {
                            title: <span>[3] Preview Details</span>,
                            type: "div",
                            isActive: true,
                        },
                    ]} />
                </div>
                

                <form onSubmit={submitForm} className="mt-6 max-w-2xl block">
                    <div>
                        Please review the business details below before adding business.
                    </div>

                    <div className="mt-6 box border rounded-none border-opacity-40">
                        <div className="text-ep-primary font-medium">
                            Business Details
                        </div>
                        <div className="flex flex-wrap">
                            <DetailsContent label="Company Name" value={subAccountForm?.companyName || "-"} />
                            <DetailsContent label="Company Size" value={subAccountForm?.companySize ? companySizeArray[subAccountForm?.companySize] + " employees" : "-"} />
                            <DetailsContent label="RC Number" value={subAccountForm?.rcNumber || "-"} />
                            <DetailsContent label="TIN Number" value={subAccountForm?.tinNumber || "-"} />
                            <DetailsContent label="Payment Email" value={subAccountForm?.invoiceEmail || "-"} />
                            <DetailsContent label="Registered address" value={subAccountForm?.address || "-"} />
                            <DetailsContent label="Number of Employees" value={subAccountForm?.estimatedEmployees || "-"} />
                            <DetailsContent label="Total Monthly Payroll" value={CurrencyFormat(subAccountForm?.estimatedSalary, "kobo") || "-"} />
                            <DetailsContent label="Salary Date (Every month)" value={numberOrdinalSuffix(subAccountForm?.payrollCutOff) || "-"} />
                            <DetailsContent label="Salary Withdrawal (%)" value={subAccountForm?.withdrawalPercent ? subAccountForm?.withdrawalPercent + "%" : "-"} />
                            <DetailsContent label="Payment Fee" value={paymentFeeByArray[subAccountForm?.paymentFee] || "-"} />
                        </div>
                    </div>
                    
                    <div className="mt-6 box border rounded-none border-opacity-40">
                        <div className="text-ep-primary font-medium">
                            Admin Details
                        </div>
                        <div className="flex flex-wrap">
                            <DetailsContent label="First Name" value={<span className="capitalize">{subAccountForm?.firstName || "-"}</span>} />
                            <DetailsContent label="Last Name" value={<span className="capitalize">{subAccountForm?.lastName || "-"}</span>} />
                            <DetailsContent label="Job Title" value={subAccountForm?.jobTitle || "-"} />
                            <DetailsContent label="Phone number" value={subAccountForm?.phoneNumber || "-"} />
                            <DetailsContent label="Email Address" value={subAccountForm?.email || "-"} />
                        </div>
                    </div>


                    <div className="mt-10">
                        <Checkbox
                            type="checkbox"
                            required={true}
                            label={<div className="text-sm text-gray-500 overflow-hidden">
                                By registering to acknowledge to accept to our&nbsp;
                                <Link to={{pathname:"https://earnipay.com/terms"}} target="_blank" className="font-bold text-gray-900 whitespace-nowrap hover:underline">Terms and Conditions</Link>
                                , and &nbsp;
                                <Link to={{pathname:"https://earnipay.com/privacy-policy"}} target="_blank" className="font-bold text-gray-900 whitespace-nowrap hover:underline">Privacy Policy</Link>
                                <span className="form-input-required">*</span>
                            </div>}
                        />
                    </div>


                    <div className="mt-6 flex justify-start space-x-4">
                        <ButtonLoader type="submit" loading={addSubAccountLoading} className="btn btn-ep-primary">
                            Add Business
                        </ButtonLoader>
                        <button type="button" onClick={() => history.goBack()} className="btn btn-transparent-primary">
                            Cancel
                        </button>
                    </div>
                    
                </form>
            </div>


        </div>
    )
}


const DetailsContent = (props) => (
    <div className="w-full sm:w-1/2 md:w-full lg:w-1/2 pr-6 mt-6">
        <div className="text-fade">
            {props.label}
        </div>
        <div className="mt-1 break-words">
            {props.value}
        </div>
    </div>
)


export default Preview;